// SPDX-License-Identifier: AGPL-3.0-or-later

import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import {makeAutoObservable} from 'mobx';

export interface GuildCounts {
	memberCount: number;
	onlineCount: number;
	fetchedAt: number;
}

const MAX_ENTRIES = 256;
const FRESH_TTL_MS = 60 * 1000;
const REQUEST_DEBOUNCE_MS = 100;

class GuildCount {
	private cache: Map<string, GuildCounts> = new Map();
	private pending: Set<string> = new Set();
	private flushTimer: NodeJS.Timeout | null = null;
	private cacheVersion = 0;

	constructor() {
		makeAutoObservable<GuildCount, 'cache' | 'pending' | 'flushTimer'>(
			this,
			{cache: false, pending: false, flushTimer: false},
			{autoBind: true},
		);
	}

	getCounts(guildId: string): GuildCounts | null {
		void this.cacheVersion;
		return this.cache.get(guildId) ?? null;
	}

	isStale(guildId: string, ttlMs: number = FRESH_TTL_MS): boolean {
		const entry = this.cache.get(guildId);
		if (!entry) return true;
		return Date.now() - entry.fetchedAt >= ttlMs;
	}

	requestCounts(
		guildId: string,
		options: {
			force?: boolean;
			ttlMs?: number;
		} = {},
	): void {
		if (!guildId) return;
		const force = options.force ?? false;
		const ttlMs = options.ttlMs ?? FRESH_TTL_MS;
		if (this.pending.has(guildId)) return;
		if (!force && !this.isStale(guildId, ttlMs)) return;
		this.pending.add(guildId);
		this.scheduleFlush();
	}

	handleCountsResponse(
		entries: ReadonlyArray<{
			guild_id: string;
			member_count: number;
			online_count: number;
		}>,
	): void {
		const now = Date.now();
		for (const entry of entries) {
			if (!entry || typeof entry.guild_id !== 'string') continue;
			this.touch(entry.guild_id, {
				memberCount: typeof entry.member_count === 'number' ? entry.member_count : 0,
				onlineCount: typeof entry.online_count === 'number' ? entry.online_count : 0,
				fetchedAt: now,
			});
			this.pending.delete(entry.guild_id);
		}
		this.cacheVersion += 1;
	}

	handleGuildDelete(guildId: string): void {
		if (this.cache.delete(guildId)) {
			this.cacheVersion += 1;
		}
		this.pending.delete(guildId);
	}

	handleSessionInvalidated(): void {
		this.cache.clear();
		this.pending.clear();
		if (this.flushTimer != null) {
			clearTimeout(this.flushTimer);
			this.flushTimer = null;
		}
		this.cacheVersion += 1;
	}

	private touch(guildId: string, counts: GuildCounts): void {
		if (this.cache.has(guildId)) {
			this.cache.delete(guildId);
		}
		this.cache.set(guildId, counts);
		while (this.cache.size > MAX_ENTRIES) {
			const oldestKey = this.cache.keys().next().value;
			if (oldestKey === undefined) break;
			this.cache.delete(oldestKey);
		}
	}

	private scheduleFlush(): void {
		if (this.flushTimer != null) return;
		this.flushTimer = setTimeout(() => {
			this.flushTimer = null;
			this.flushPending();
		}, REQUEST_DEBOUNCE_MS);
	}

	private flushPending(): void {
		if (this.pending.size === 0) return;
		const guildIds = Array.from(this.pending);
		const socket = GatewayConnection.socket;
		if (!socket) {
			this.pending.clear();
			return;
		}
		socket.requestGuildCounts(guildIds);
	}
}

export default new GuildCount();
